import classNames from "classnames";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";

import styles from "./index.module.scss";

interface IRangeSliderProps {
  min: number;
  max: number;
  onChange?: (value: { min: number; max: number }) => void;
  onMouseUp?: (value: { min: number; max: number }) => void;
  showLabel?: boolean;
  className?: string;
}

function RangeSlider({
  onChange,
  onMouseUp,
  min = 0,
  max = 100,
  showLabel = false,
  className = "",
}: IRangeSliderProps) {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);

  const minValRef = useRef(min);
  const maxValRef = useRef(max);

  const range = useRef<HTMLDivElement>(null);

  const inputMin = useRef<HTMLInputElement>(null);
  const inputMax = useRef<HTMLInputElement>(null);

  const handleSliderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    const clickXInsideElement = event.clientX - rect.left;

    const trackClick = (() => {
      if (clickXInsideElement / (element.clientWidth / 100) >= 100) {
        return 100;
      }

      if (clickXInsideElement / (element.clientWidth / 100) <= 0) {
        return 0;
      }

      return clickXInsideElement / (element.clientWidth / 100);
    })();

    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);

    if (Math.abs(trackClick - minPercent) < Math.abs(trackClick - maxPercent)) {
      if (inputMin.current) {
        const newValue = Math.min(
          (trackClick / 100) * (max - min) + min,
          maxVal - 1,
        );

        setMinVal(newValue);
        minValRef.current = newValue;

        if (onChange) {
          onChange({ min: newValue, max: maxVal });
        }

        if (onMouseUp) {
          onMouseUp({ min: newValue, max: maxVal });
        }
      }
    } else {
      if (inputMax.current) {
        const newValue = Math.max(
          (trackClick / 100) * (max - min) + min,
          minVal + 1,
        );

        setMaxVal(newValue);
        maxValRef.current = newValue;

        if (onChange) {
          onChange({ min: minVal, max: newValue });
        }

        if (onMouseUp) {
          onMouseUp({ min: minVal, max: newValue });
        }
      }
    }
  };

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [minVal, getPercent]);

  useEffect(() => {
    const minPercent = getPercent(minValRef.current);
    const maxPercent = getPercent(maxVal);

    if (range.current) {
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, getPercent]);

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={handleSliderClick}>
      <input
        type="range"
        min={min}
        max={max}
        value={minVal}
        onMouseUp={() => {
          if (onMouseUp) {
            onMouseUp({ min: minVal, max: maxVal });
          }
        }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = Math.min(Number(event.target.value), maxVal - 1);

          setMinVal(value);
          minValRef.current = value;

          if (onChange) {
            onChange({ min: value, max: maxVal });
          }
        }}
        ref={inputMin}
        className={classNames(styles.thumb, styles.thumb__left)}
        style={{ zIndex: minVal > max - 100 ? 5 : undefined }}
      />

      <input
        ref={inputMax}
        type="range"
        min={min}
        max={max}
        value={maxVal}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = Math.max(Number(event.target.value), minVal + 1);

          setMaxVal(value);
          maxValRef.current = value;

          if (onChange) {
            onChange({ min: minVal, max: value });
          }
        }}
        onMouseUp={() => {
          if (onMouseUp) {
            onMouseUp({ min: minVal, max: maxVal });
          }
        }}
        className={classNames(styles.thumb, styles.thumb__right)}
      />

      <div className={classNames(styles.slider)}>
        <div className={classNames(styles.slider__track)}></div>
        <div ref={range} className={classNames(styles.slider__range)}></div>
        {showLabel && (
          <React.Fragment>
            <div className={classNames(styles.slider__left__value)}>
              {minVal}
            </div>
            <div className={classNames(styles.slider__right__value)}>
              {maxVal}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default RangeSlider;
